import { createContext, useContext, useRef, useCallback, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  playArcadeTransition,
  playLoginTransition,
  playLogoutTransition,
  type TransitionMode,
} from '@/shared/utils/arcadeTransitionAnimation';
import { getArcadeSounds } from '@/shared/utils/ArcadeSounds';

interface ArcadeTransitionContextValue {
  triggerTransition: (
    targetPath: string,
    mode: TransitionMode,
    onBeforeNavigate?: () => void,
  ) => void;
}

const ArcadeTransitionContext = createContext<ArcadeTransitionContextValue | null>(null);

const GRID_SIZE = 10;
const TILE_COUNT = GRID_SIZE * GRID_SIZE;

const MODE_LABEL_KEYS: Record<TransitionMode, string> = {
  admin: 'transition.adminMode',
  home: 'transition.homeMode',
  login: 'transition.welcome',
  logout: 'transition.goodbye',
};

export function ArcadeTransitionProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const tilesRef = useRef<(HTMLDivElement | null)[]>([]);
  const textRef = useRef<HTMLSpanElement>(null);
  const scanlinesRef = useRef<HTMLDivElement>(null);

  const triggerTransition = useCallback(
    (targetPath: string, mode: TransitionMode, onBeforeNavigate?: () => void) => {
      if (isTransitioning) return;
      if (!overlayRef.current || !textRef.current || !scanlinesRef.current) return;

      const tiles = tilesRef.current.filter(Boolean) as HTMLElement[];
      if (tiles.length !== TILE_COUNT) return;

      setIsTransitioning(true);

      // Update label text via i18n
      textRef.current.textContent = t(MODE_LABEL_KEYS[mode]);

      // Play sound by mode
      const sounds = getArcadeSounds();
      if (sounds) {
        if (mode === 'login') sounds.playBootUp();
        else if (mode === 'logout') sounds.playShutDown();
        else sounds.playLevelUp();
      }

      // Midpoint: run pre-navigate callback then navigate
      const onMidpoint = () => {
        onBeforeNavigate?.();
        navigate(targetPath);
      };

      const onComplete = () => setIsTransitioning(false);

      // Route animation function by mode
      if (mode === 'login') {
        playLoginTransition(tiles, textRef.current, scanlinesRef.current, overlayRef.current, onMidpoint, onComplete);
      } else if (mode === 'logout') {
        playLogoutTransition(tiles, textRef.current, scanlinesRef.current, overlayRef.current, onMidpoint, onComplete);
      } else {
        playArcadeTransition(tiles, textRef.current, scanlinesRef.current, overlayRef.current, mode, onMidpoint, onComplete);
      }
    },
    [isTransitioning, navigate, t],
  );

  const contextValue = useMemo(() => ({ triggerTransition }), [triggerTransition]);

  const overlay = createPortal(
    <div ref={overlayRef} className="arcade-transition-overlay">
      <div className="arcade-transition-grid">
        {Array.from({ length: TILE_COUNT }, (_, i) => (
          <div
            key={i}
            ref={(el) => { tilesRef.current[i] = el; }}
            className="arcade-tile"
          />
        ))}
      </div>
      <div className="arcade-transition-text">
        <span ref={textRef} />
      </div>
      <div ref={scanlinesRef} className="arcade-transition-scanlines" />
    </div>,
    document.body,
  );

  return (
    <ArcadeTransitionContext.Provider value={contextValue}>
      {children}
      {overlay}
    </ArcadeTransitionContext.Provider>
  );
}

export function useArcadeTransition() {
  const ctx = useContext(ArcadeTransitionContext);
  if (!ctx) throw new Error('useArcadeTransition must be used within ArcadeTransitionProvider');
  return ctx;
}
